import { Router } from '@angular/router'
import { Injectable, OnDestroy } from '@angular/core'
import { HttpClient } from '@angular/common/http'
import { Title } from '@angular/platform-browser'
import { JwtHelperService } from '@auth0/angular-jwt';

import { UserLoginModel, UserLoginResponse } from './user'
import { environment } from '../../../environments/environment'

interface UserProfile {
  id: number
  nome: string
  permissoes: string[]
}

interface OwnerSession {
  access_token: string
  user_profile: string | null
  account_id: string
  oon_integration_enabled: string | null
}

const BASE_TITLE = 'Hudly.go'
const OWNER_SESSION_KEY = 'owner_session'
const LOGOUT_BROADCAST_KEY = 'logout_broadcast'

@Injectable({
  providedIn: 'root'
})
export class AuthService implements OnDestroy {

  roleProfile: UserProfile

  private readonly logoutBroadcastListener = (event: StorageEvent) => this.handleLogoutBroadcast(event)

  constructor(
    private httpClient: HttpClient,
    private router: Router,
    private title: Title,
    private jwtHelper: JwtHelperService) {
      window.addEventListener('storage', this.logoutBroadcastListener)
    }

  ngOnDestroy(): void {
    window.removeEventListener('storage', this.logoutBroadcastListener)
  }

  set token(token: string) {
    sessionStorage.setItem('access_token', token)
  }

  get token(): string {
    return sessionStorage.getItem('access_token') || ''
  }

  savePerfil(perfil: any) {
    sessionStorage.setItem('user_profile', JSON.stringify(perfil))
  }

  getPerfil(): any {
    let profile = sessionStorage.getItem('user_profile')
    if (profile) profile = JSON.parse(profile)
    return profile
  }

  /**
   * Conta acessada pela aba. Mantido no sessionStorage para que cada aba
   * permaneça na conta que abriu.
   */
  set accountId(accountId: string) {
    if (accountId) sessionStorage.setItem('account_id', accountId)
    else sessionStorage.removeItem('account_id')
  }

  get accountId(): string {
    return sessionStorage.getItem('account_id') || ''
  }

  set accountName(accountName: string) {
    if (accountName) sessionStorage.setItem('account_name', accountName)
    else sessionStorage.removeItem('account_name')
  }

  get accountName(): string {
    return sessionStorage.getItem('account_name') || ''
  }

  set firstAccess(isFirstAccess: boolean) {
    if (isFirstAccess) sessionStorage.setItem('isLogging', 'true')
    else sessionStorage.removeItem('isLogging')
  }

  get firstAccess(): boolean {
    return !!sessionStorage.getItem('isLogging')
  }

  /**
   * Exibe a conta acessada no título da aba do navegador.
   */
  applyAccountTitle(): void {
    const accountName = this.accountName
    this.title.setTitle(accountName ? `${accountName} - ${BASE_TITLE}` : BASE_TITLE)
  }

  /**
   * Guarda a sessão owner da aba antes de assumir o acesso a uma conta,
   * para que o "Sair" retorne esta aba à listagem de contas.
   */
  saveOwnerSession(): void {
    const ownerSession: OwnerSession = {
      access_token: this.token,
      user_profile: sessionStorage.getItem('user_profile'),
      account_id: this.accountId,
      oon_integration_enabled: sessionStorage.getItem('oon_integration_enabled')
    }
    sessionStorage.setItem(OWNER_SESSION_KEY, JSON.stringify(ownerSession))
  }

  hasOwnerSession(): boolean {
    return !!sessionStorage.getItem(OWNER_SESSION_KEY)
  }

  discardOwnerSession(): void {
    sessionStorage.removeItem(OWNER_SESSION_KEY)
  }



  isLogged(): boolean {
    return !!this.token
  }

  isAdmin(): boolean {

    if (!this.token) return false

    const Decoded = this.decodeJwtToken(this.token)
    const adminStatus: boolean = Decoded.sub.is_admin

    return adminStatus || false

  }

  isAdminProfile() {
    const profile = this.getPerfil()
    return profile.id === 1
  }

  /**
   * Verificação única de acesso owner: conta principal ou acesso owner a uma conta.
   * Vem do claim do próprio token, então não há como divergir da sessão do back.
   */
  isOwnerAccess(): boolean {
    if (!this.token) return false

    try {
      return this.decodeJwtToken(this.token)?.sub?.is_owner_access === true
    } catch {
      return false
    }
  }

  handleSaveProfile(token:string): void {

    const Decoded = this.decodeJwtToken(token)
    const profile: UserProfile = Decoded.sub.perfil
    this.savePerfil(profile)

  }

  decodeJwtToken(token:string): any {
    return this.jwtHelper.decodeToken(token)
  }

  isJwtExpValid(exp: number): boolean {

    let now = new Date()
    let expTime = new Date(exp * 1000);

    return now < expTime;

  }

  login(user: UserLoginModel) {
    return this.httpClient.post<UserLoginResponse>(environment.apiUrl + '/login', user)
  }

  logout() {
    // Somente em aba de acesso owner a uma conta sair retorna à listagem de contas,
    // sem encerrar a sessão das demais abas. Nos demais casos a sessão é encerrada.
    if (this.isOwnerAccess() && this.hasOwnerSession()) {
      this.restoreOwnerSession()
      this.router.navigateByUrl('/admin')
      return
    }

    this.clearTabSession()
    localStorage.setItem(LOGOUT_BROADCAST_KEY, String(Date.now()))

    this.router.navigateByUrl('/auth')
  }

  /**
   * Devolve a aba à sessão owner guardada ao acessar a conta.
   */
  private restoreOwnerSession(): void {
    const storedOwnerSession = sessionStorage.getItem(OWNER_SESSION_KEY)
    if (!storedOwnerSession) return

    const ownerSession: OwnerSession = JSON.parse(storedOwnerSession)

    this.token = ownerSession.access_token
    this.accountId = ownerSession.account_id
    this.accountName = ''

    if (ownerSession.user_profile) sessionStorage.setItem('user_profile', ownerSession.user_profile)
    else sessionStorage.removeItem('user_profile')

    if (ownerSession.oon_integration_enabled) sessionStorage.setItem('oon_integration_enabled', ownerSession.oon_integration_enabled)
    else sessionStorage.removeItem('oon_integration_enabled')

    sessionStorage.removeItem(OWNER_SESSION_KEY)
    this.applyAccountTitle()
  }

  /**
   * Encerra a sessão desta aba, sem afetar o estado das demais.
   */
  private clearTabSession(): void {
    sessionStorage.clear()
    this.applyAccountTitle()

    // Remover JSESSIONID do cookie
    try {
      document.cookie = 'JSESSIONID=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT'
    } catch {}
  }

  /**
   * Encerrar a sessão em uma aba encerra o acesso em todas as abas.
   */
  private handleLogoutBroadcast(event: StorageEvent): void {
    if (event.key !== LOGOUT_BROADCAST_KEY || !event.newValue) return

    // Abas sem sessão (ex.: área do cliente e links externos) não são afetadas.
    if (!this.isLogged()) return

    this.clearTabSession()
    this.router.navigateByUrl('/auth')
  }

  recoveryPassword(userForRecovery: any) {
    return this.httpClient.post<UserLoginResponse>(environment.apiUrl + '/login/recovery', userForRecovery)
  }

  getAccountType() {
    return this.httpClient.get<{account_type_name: string, oon_integration_enabled: boolean}>(environment.apiUrl + '/account/type')
  }




}
